import { styled } from "@linaria/react";
import React from "react";
import {
  isActiveAnalysis,
  useActiveAnalysisCount,
  type ActiveAnalysis,
} from "../../../api/useActiveAnalysesStream";
import type { Project } from "../../../api/useProjects";
import useModal from "@ui/lib/hooks/useModal";
import t from "../../../i18n";
import { color } from "@ui";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import ToggleActiveModalForm from "./ToggleActiveModalForm";
import useSelectedAnalyses from "./useSelectedAnalyses";
import type { Analysis } from "../../../api/Analysis";
import ButtonIconV2 from "@ui/ButtonIconV2";
import {
  BookmarkLine,
  MoreFill,
  FlashlightFill,
  CloseCircleLine,
  FlashlightSlashFill,
  Chat4Line,
  DownloadLine,
  FlagFill,
  FlagLine,
  ShareBoxLine,
} from "@ui/IconV2/react";
import type { PublicationAction } from "./PublicationModalForm";
import PublicationModalForm from "./PublicationModalForm";
import ShareCircleLine from "@ui/IconV2/react/ShareCircleLine";
import { useSidePanelOpened } from "../useSidePanel";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import AnalysisExportModalForm from "./AnalysisExportModalForm";
import useSnapshotCount from "../../../api/useSnapshotCount";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import type { iconSizes } from "@ui/IconV2/react/utils/shared";
import { analysisCompliant } from "diagnostics-domain/quantity/compliance";
import { useQuantities } from "../../../api/useQuantities";
import AnalysisFilterCompliant from "./AnalysisFilterCompliant";
import IndicatorsDisplay from "./IndicatorDisplay";
import { isAnalysisQuantityValid } from "../metrology";
import DeactivateAnalysesModalForm from "./Forms/DeactivateAnalysesModalForm";
import ActivateAnalysesModalForm from "./Forms/ActivateAnalysesModalForm";
import { useSettings } from "../../../api/useSettings";
import { toPath } from "../../../router/helpers/path";
import { createManualFaultEventDrawerParamURL } from "../Events/utils/event";
import type { Event } from "../../../api/useEvents";
import { getEventEndsAt } from "../../../domain/event";
import ModalV2 from "@ui/ModalV2";

interface P {
  project: Project;
  event: Event | null;
  analyses: (Analysis | ActiveAnalysis)[];
  canWriteProject: boolean;
  canUserReadProject: boolean;
  allAnalysesMode: boolean;
  className?: string;
  openFailureManagementModal?: () => void;
}

const ActionMenu = ({
  event,
  analyses,
  project,
  canWriteProject,
  canUserReadProject,
  allAnalysesMode,
  openFailureManagementModal,
  className,
}: P) => {
  const settings = useSettings();
  const quantities = useQuantities(project.id);

  const [sidePanelOpened, setSidePanelOpened] = useSidePanelOpened({ scope: project.id });
  const { boundary, selectedAnalyses } = useSelectedAnalyses(project.id, analyses);
  const hasBoundaryPivot = Boolean(boundary.pivot);

  const boundaryDates = React.useMemo(() => {
    const { pivot, to } = boundary;
    if (pivot === null || to === null) return null;

    return {
      from: pivot < to ? new Date(pivot) : new Date(to),
      to: pivot < to ? new Date(to) : new Date(pivot),
    };
  }, [boundary]);

  const activeAnalysisCount = useActiveAnalysisCount(project.id, boundaryDates);
  const snapshotCount = useSnapshotCount(project.id, boundaryDates);
  const areAllAnalysesActive = React.useMemo(
    () => selectedAnalyses.every(isActiveAnalysis),
    [selectedAnalyses],
  );

  const processIndicators = React.useMemo(
    () =>
      quantities
        .filter(q => q.processIndicatorOrder !== null)
        .sort((a, b) => (a.processIndicatorOrder ?? 0) - (b.processIndicatorOrder ?? 0)),
    [quantities],
  );

  const indicatorQuantityValues = React.useMemo(() => {
    if (selectedAnalyses.length === 1) {
      const analysis = selectedAnalyses[0];
      if (!analysis) throw new Error("Analysis not found");
      const validProcessIndicators = processIndicators.filter(quantity =>
        isActiveAnalysis(analysis) ? isAnalysisQuantityValid(analysis, quantity) : true,
      );

      return [
        Object.fromEntries(
          Object.entries(analysis.quantityValues).filter(([key]) =>
            validProcessIndicators.some(q => q.id === key),
          ),
        ),
      ];
    }
    return [];
  }, [processIndicators, selectedAnalyses]);

  const indicators = React.useMemo(
    () =>
      processIndicators.map(indicator => ({
        name: indicator.name,
        measured: indicatorQuantityValues[0]?.[indicator.id]?.measured ?? null,
        unit: indicator.unit || "",
      })),
    [processIndicators, indicatorQuantityValues],
  );

  const isAnalysesOutsideEventPeriod = React.useMemo(() => {
    if (event === null) return false;

    const dates = selectedAnalyses.map(a => a.date);
    const minAnalysisDate = Math.min(...dates);
    const maxAnalysisDate = Math.max(...dates);
    const endsAt = getEventEndsAt(event);

    return minAnalysisDate < event.startsAt.getTime() || maxAnalysisDate > endsAt.getTime();
  }, [event, selectedAnalyses]);

  const title = React.useMemo(() => {
    const hasNoVisbleAnalysis = selectedAnalyses.length === 0 && hasBoundaryPivot;
    const hasNoDataPointsSelected = selectedAnalyses.length === 0 && !hasBoundaryPivot;
    const hasSingleSelectedAnalysis = selectedAnalyses.length === 1;
    const hasMultipleSelectedAnalyses = selectedAnalyses.length > 1;

    let titleElement: React.ReactNode = null;

    if (hasNoVisbleAnalysis)
      titleElement = allAnalysesMode ? (
        <Text type="body1SemiBold" className={selectionNotActive}>
          {t("measures.chart.not.visible.analysis")}
        </Text>
      ) : (
        <Text type="body1SemiBold" className={selectionNotActive}>
          {t("measures.chart.not.used.analysis")}
        </Text>
      );
    else if (hasNoDataPointsSelected)
      titleElement = (
        <Text type="body1" className={selectionNotActive}>
          {t("project.charts.title.no_data_points_selected", {
            showInactiveAnalyses: allAnalysesMode,
          })}
        </Text>
      );
    else if (hasSingleSelectedAnalysis)
      titleElement = (
        <Text type="body1">
          {t("project.charts.title.selected_points", {
            selectedPointsText: (
              <Text type="body1SemiBold" as="span">
                {t("project.charts.title.selected_points.one.details", {
                  showInactiveAnalyses: allAnalysesMode,
                })}
              </Text>
            ),
          })}
        </Text>
      );
    else if (hasMultipleSelectedAnalyses)
      titleElement = (
        <Text type="body1">
          {t("project.charts.title.selected_points", {
            selectedPointsText: (
              <Text type="body1SemiBold" as="span">
                {t("measures.chart.selected.analyses", {
                  showInactiveAnalyses: allAnalysesMode,
                  activeAnalysisCount,
                  snapshotCount,
                })}
              </Text>
            ),
          })}
        </Text>
      );
    else titleElement = null;

    const flagColor =
      selectedAnalyses.length === 0 && hasBoundaryPivot ? color.titanium400 : color.darkNitrogen600;
    const showFlag =
      selectedAnalyses.length === 0 && hasBoundaryPivot && allAnalysesMode ? false : true;
    const flagProps = {
      size: "m" as keyof typeof iconSizes,
      color: flagColor,
    };

    return (
      <div className={titleWrapper}>
        {showFlag ? (
          hasNoDataPointsSelected ? (
            <FlagLine {...flagProps} />
          ) : (
            <FlagFill {...flagProps} />
          )
        ) : null}
        {titleElement}
        {isAnalysesOutsideEventPeriod ? (
          <Text className={analysisOutsidePeriodEvent}>
            {t("project.chart.analysis.outside_event_period")}
          </Text>
        ) : null}
      </div>
    );
  }, [
    activeAnalysisCount,
    snapshotCount,
    hasBoundaryPivot,
    allAnalysesMode,
    selectedAnalyses.length,
    isAnalysesOutsideEventPeriod,
  ]);

  const activationButtonLabel = (() => {
    if (selectedAnalyses.length > 1)
      return t("measures.chart.selected.analyses.activate.deactivate.button");
    if (areAllAnalysesActive) return t("commons.button.deactivate");
    return t("commons.button.activate");
  })();

  const {
    modal: isToggleActiveAnalysisModalOpen,
    openModal: openToggleActiveAnalysisModal,
    closeModal: closeToggleActiveAnalysisModal,
  } = useModal();

  const {
    modal: isActivateAnalysisModalOpen,
    openModal: openActivateAnalysisModal,
    closeModal: closeActivateAnalysisModal,
  } = useModal();

  const {
    modal: isDeactivateAnalysisModalOpen,
    openModal: openDeactivateAnalysisModal,
    closeModal: closeDeactivateAnalysisModal,
  } = useModal();

  const {
    modal: isPublicationModalOpen,
    openModal: openPublicationModal,
    closeModal: closePublicationModal,
  } = useModal();

  const {
    modal: isAnalysisExport,
    openModal: openAnalysisExport,
    closeModal: closeAnalysisExport,
  } = useModal();

  const someAnalysesPublished = selectedAnalyses.some(a => "isPublished" in a && a.isPublished);
  const someAnalysesUnpublished = selectedAnalyses.some(a => "isPublished" in a && !a.isPublished);

  const [publicationModalAction, setPublicationModalAction] =
    React.useState<PublicationAction>("publish");

  const openPublicationModalWithAction = React.useCallback(
    (action: PublicationAction) => {
      setPublicationModalAction(action);
      openPublicationModal();
    },
    [openPublicationModal],
  );

  const handleToggleActiveAnalysisHotkey = React.useCallback(
    (e: KeyboardEvent) => {
      if (
        !document.activeElement?.className.includes("highcharts-root") &&
        !document.activeElement?.querySelector(".highcharts-root")
      )
        return;
      if (e.key === "Backspace" || e.key === "Delete") {
        if (selectedAnalyses.length === 1) openToggleActiveAnalysisModal();
        if (selectedAnalyses.length > 1) {
          openDeactivateAnalysisModal();
        }
      }
    },
    [openToggleActiveAnalysisModal, openDeactivateAnalysisModal, selectedAnalyses.length],
  );

  React.useEffect(() => {
    window.addEventListener("keyup", handleToggleActiveAnalysisHotkey, { passive: true });
    return () => {
      window.removeEventListener("keyup", handleToggleActiveAnalysisHotkey);
    };
  }, [handleToggleActiveAnalysisHotkey]);

  const firstAnalysis = selectedAnalyses[0];
  if (selectedAnalyses.length === 0 && !hasBoundaryPivot) return null;

  const showAnalysisActivate = allAnalysesMode && selectedAnalyses.length === 1;
  const showOptionsNotAllData = !allAnalysesMode || selectedAnalyses.length > 1;

  const shouldDisplayIndicators = selectedAnalyses.length === 1 && areAllAnalysesActive;
  const showRedirectionOption = canWriteProject && settings.MANUAL_EVENTS_DEV_MODE_ENABLED;
  const showManualFailureOption = canWriteProject && openFailureManagementModal !== undefined;
  const canPublishAnalyses = settings.FLEET_VIEW_ENABLED && canWriteProject;

  return (
    <React.Suspense fallback="">
      <Wrapper className={className}>
        {title}
        {selectedAnalyses.length > 0 && (!allAnalysesMode || canWriteProject) && (
          <>
            {canWriteProject &&
              showAnalysisActivate &&
              (areAllAnalysesActive ? (
                <TooltipV2
                  placement="right"
                  overlay={
                    <OverlayWrapper text={t("measures.chart.selected.analysis.activated.date")} />
                  }
                >
                  <ButtonIconV2 onClick={openToggleActiveAnalysisModal}>
                    <FlashlightFill color={color.lightNitrogen500_Main} size="m" />
                  </ButtonIconV2>
                </TooltipV2>
              ) : (
                <TooltipV2
                  placement="right"
                  overlay={
                    <OverlayWrapper text={t("measures.chart.selected.analysis.deactivated.date")} />
                  }
                >
                  <ButtonIconV2 onClick={openToggleActiveAnalysisModal}>
                    <FlashlightSlashFill color={color.lightNitrogen500_Main} size="m" />
                  </ButtonIconV2>
                </TooltipV2>
              ))}
            {showOptionsNotAllData && (
              <Menu
                arrowWidth={0}
                overlay={
                  <OptionGroup>
                    {showRedirectionOption ? (
                      <Option
                        className={manualFaultEventOption}
                        closeOnClick
                        to={`${toPath("/projects/:projectId/fault-events", {
                          projectId: project.id,
                        })}?${createManualFaultEventDrawerParamURL}=true`}
                        target="_blank"
                      >
                        <BookmarkLine size="s" />
                        <div>
                          <Text type="body2">
                            {t(
                              "project.diagnosis.action_menu.option.redirection_events_page.title",
                            )}
                          </Text>
                          <Text type="body3">
                            {t(
                              "project.diagnosis.action_menu.option.redirection_events_page.description",
                            )}
                          </Text>
                        </div>
                        <ShareBoxLine size="s" />
                      </Option>
                    ) : showManualFailureOption ? (
                      <Option closeOnClick onClick={openFailureManagementModal}>
                        <BookmarkLine size="s" />
                        {t("diagnosis.header.dropdown.manage_manual_failure")}
                      </Option>
                    ) : null}
                    {canWriteProject &&
                      !sidePanelOpened &&
                      selectedAnalyses.length === 1 &&
                      event === null && (
                        <Option closeOnClick onClick={() => setSidePanelOpened(true)}>
                          <Chat4Line size="s" />
                          {t("common.button.add_comment_on_analysis")}
                        </Option>
                      )}
                    {canUserReadProject && !allAnalysesMode && (
                      <Option closeOnClick onClick={openAnalysisExport}>
                        <DownloadLine size="s" />
                        {t("common.button.export_analysis")}
                      </Option>
                    )}
                    {canPublishAnalyses && someAnalysesUnpublished && areAllAnalysesActive && (
                      <Option
                        closeOnClick
                        onClick={() => openPublicationModalWithAction("publish")}
                      >
                        <TooltipV2
                          placement="right"
                          overlay={
                            <OverlayWrapper
                              text={t("measures.chart.selected.analysis.activated.date")}
                            />
                          }
                        >
                          <TooltipWrapper>
                            <ShareCircleLine size="s" color={color.carbon800} />
                          </TooltipWrapper>
                        </TooltipV2>
                        {t("commons.button.publish")}
                      </Option>
                    )}
                    {canPublishAnalyses && someAnalysesPublished && areAllAnalysesActive && (
                      <Option
                        closeOnClick
                        onClick={() => openPublicationModalWithAction("unpublish")}
                      >
                        <TooltipV2
                          placement="right"
                          overlay={
                            <OverlayWrapper
                              text={t("measures.chart.selected.analysis.activated.date")}
                            />
                          }
                        >
                          <TooltipWrapper>
                            <CloseCircleLine size="s" color={color.carbon800} />
                          </TooltipWrapper>
                        </TooltipV2>
                        {t("commons.button.unpublish")}
                      </Option>
                    )}
                    {canWriteProject && (
                      <>
                        {boundaryDates === null ? (
                          <Option closeOnClick onClick={openToggleActiveAnalysisModal}>
                            <FlashlightSlashFill size="s" color={color.carbon800} />

                            {activationButtonLabel}
                          </Option>
                        ) : (
                          <>
                            <Option
                              closeOnClick
                              title={
                                activeAnalysisCount === snapshotCount
                                  ? t(
                                      "measures.chart.selected.analyses.activate.button.disabled.tooltip",
                                    )
                                  : undefined
                              }
                              disabled={activeAnalysisCount === snapshotCount}
                              onClick={openActivateAnalysisModal}
                            >
                              <FlashlightFill size="s" color={color.carbon800} />
                              {t("measures.chart.selected.analyses.activate.button.name")}
                            </Option>
                            <Option
                              closeOnClick
                              onClick={openDeactivateAnalysisModal}
                              disabled={activeAnalysisCount === 0}
                              title={
                                activeAnalysisCount === 0
                                  ? t(
                                      "measures.chart.selected.analyses.deactivate.button.name.disabled.no_active_analysis",
                                    )
                                  : undefined
                              }
                            >
                              <FlashlightFill size="s" color={color.carbon800} />
                              {t("measures.chart.selected.analyses.deactivate.button.name")}
                            </Option>
                          </>
                        )}
                      </>
                    )}
                  </OptionGroup>
                }
              >
                <ButtonIconV2>
                  <MoreFill />
                </ButtonIconV2>
              </Menu>
            )}
            {selectedAnalyses.length === 1 && firstAnalysis ? (
              <AnalysisFilterCompliant
                project={project}
                quantities={quantities}
                analysis={firstAnalysis}
              />
            ) : null}
            {canUserReadProject && (
              <ModalV2
                title={t("project.charts.action.menu.modal.analysis_export.title")}
                isOpen={isAnalysisExport}
                onRequestClose={closeAnalysisExport}
              >
                <AnalysisExportModalForm
                  project={project}
                  selectedAnalyses={selectedAnalyses}
                  closeModal={closeAnalysisExport}
                />
              </ModalV2>
            )}
            {canWriteProject && boundaryDates && (
              <>
                <ModalV2
                  title={t("measures.chart.selected.analyses.modal.activate.title")}
                  isOpen={isActivateAnalysisModalOpen}
                  onRequestClose={closeActivateAnalysisModal}
                >
                  <ActivateAnalysesModalForm
                    from={boundaryDates.from}
                    to={boundaryDates.to}
                    activeAnalysesCount={activeAnalysisCount}
                    dataPointsCount={snapshotCount}
                    closeModal={closeActivateAnalysisModal}
                    projectId={project.id}
                  />
                </ModalV2>
                <ModalV2
                  title={t("measures.chart.selected.analyses.modal.deactivate.title")}
                  isOpen={isDeactivateAnalysisModalOpen}
                  onRequestClose={closeDeactivateAnalysisModal}
                >
                  <DeactivateAnalysesModalForm
                    from={boundaryDates.from}
                    to={boundaryDates.to}
                    activeAnalysesCount={activeAnalysisCount}
                    dataPointsCount={snapshotCount}
                    closeModal={closeDeactivateAnalysisModal}
                    projectId={project.id}
                  />
                </ModalV2>
              </>
            )}
            {canWriteProject && selectedAnalyses[0] && (
              <ModalV2
                title={t("measures.chart.analysis.modal.toggle.analysis.title", {
                  active: areAllAnalysesActive,
                })}
                isOpen={isToggleActiveAnalysisModalOpen}
                onRequestClose={closeToggleActiveAnalysisModal}
              >
                <ToggleActiveModalForm
                  analysisId={selectedAnalyses[0].id}
                  compliant={analysisCompliant(quantities, selectedAnalyses[0])}
                  projectId={project.id}
                  closeModal={closeToggleActiveAnalysisModal}
                  isActive={areAllAnalysesActive}
                />
              </ModalV2>
            )}
            {canWriteProject && areAllAnalysesActive && (
              <ModalV2
                title={t("project.charts.confirm_analysis_publish_modal.title", {
                  action: publicationModalAction,
                })}
                subtitle={t("project.charts.confirm_analysis_publish_modal.subtitle", {
                  count: selectedAnalyses.length,
                  action: publicationModalAction,
                })}
                isOpen={isPublicationModalOpen}
                onRequestClose={closePublicationModal}
              >
                <PublicationModalForm
                  closeModal={closePublicationModal}
                  selectedAnalyses={selectedAnalyses}
                  projectTimezone={project.timezone}
                  projectId={project.id}
                  action={publicationModalAction}
                />
              </ModalV2>
            )}
          </>
        )}
      </Wrapper>
      {shouldDisplayIndicators && <IndicatorsDisplay indicators={indicators} />}
    </React.Suspense>
  );
};

export default React.memo(ActionMenu);

//#region CSS
const Wrapper = styled.div`
  display: flex;
  column-gap: 8px;
  align-items: center;
  height: 32px;
`;
const TooltipWrapper = styled.span`
  display: grid;
  place-items: center;
  margin-right: ${spacings.xxs};
`;
const titleWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
const selectionNotActive = css`
  color: ${colors.carbon700};
`;
const analysisOutsidePeriodEvent = css`
  color: ${colors.carbon700};
`;
const manualFaultEventOption = css`
  height: 52px;
  align-items: flex-start;

  & > :nth-child(2) {
    & > :last-child {
      color: ${color.carbon700};
    }
  }
`;
//#endregion CSS
